import "./ra-dashboard-content.scss";

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { generatePath } from "react-router-dom";

import { AssessmentAPI } from "@/api/legacy/risk-assessment/AssessmentApi";
import { Typography } from "@/components/Elements";
import Loading from "@/components/Loading";
import { useMainStore } from "@/contexts/Store";

import { RISK_ASSESSMENT_STATUS_DISPLAY } from "../../config";
import { ROUTES } from "../../pages";
import { AssessmentIndex } from "../../types/assessment";
import RADashboardAssessments from "./Assessments/RADashboardAssessments";
import Shortcuts from "./Shortcuts/Shortcuts";

type Props = {
  selectedAssessmentTemplateId: number;
  onSelectedAssessmentIdChange: (id: number) => void;
};

function RADashboardContent(props: Props) {
  const mainStore = useMainStore();
  const { workspaceID } = mainStore.context;
  const [isLoading, setIsLoading] = useState(true);
  const [partnerAssessments, setPartnerAssessments] = useState<
    AssessmentIndex[]
  >([]);

  useEffect(() => {
    if (!workspaceID) {
      return;
    }

    AssessmentAPI.getAll(workspaceID)
      .then((assessments) => {
        assessments.sort((a, b) => {
          const aOrder = RISK_ASSESSMENT_STATUS_DISPLAY[a.status].order;
          const bOrder = RISK_ASSESSMENT_STATUS_DISPLAY[b.status].order;
          if (aOrder < bOrder) {
            return -1;
          } else if (aOrder > bOrder) {
            return 1;
          }

          const aPartnerName = a.partner_name?.toLowerCase();
          const bPartnerName = b.partner_name?.toLowerCase();
          if (aPartnerName < bPartnerName) {
            return -1;
          } else if (aPartnerName > bPartnerName) {
            return 1;
          }

          return a.name < b.name ? -1 : 1;
        });
        setPartnerAssessments(assessments);
      })
      .catch(() =>
        mainStore.toast.setErrorText(
          "There was an error loading the dashboard. Please retry.",
        ),
      )
      .finally(() => setIsLoading(false));
  }, [workspaceID]);

  if (isLoading) {
    return <Loading loadingLayout="table" />;
  }

  return (
    <div className="ra-dashboard-content">
      <div className="ra-dashboard-content__main-content">
        {partnerAssessments.length > 0 ? (
          <RADashboardAssessments
            assessments={partnerAssessments}
            selectedAssessmentTemplateId={props.selectedAssessmentTemplateId}
            onSelectedAssessmentIdChange={props.onSelectedAssessmentIdChange}
          />
        ) : (
          <>
            <header>
              <Typography
                label="Welcome to Questionnaires Module"
                color="generalMidnightDark"
                weight="semiBold"
                size="lg"
              />
              <Typography
                label="The Questionnaires module allows you to assess your partners, identify your riskiest partners, and much more!"
                color="generalMidnightDark"
              />
            </header>
            <Typography
              className="ra-dashboard-content__no-content-message"
              label="You have no assessments yet. Please select from the shortcuts to the right to get started"
            />
          </>
        )}
      </div>
      {partnerAssessments.length == 0 && (
        <div className="ra-dashboard-content__side-content">
          <Shortcuts
            shortcuts={[
              {
                to: generatePath(ROUTES.METHODOLOGY_PATH, {
                  workspace_id: Number(workspaceID),
                }),
                text: "Setup Risk Areas",
              },
              {
                to: generatePath(ROUTES.METHODOLOGY_CREATION_PATH, {
                  workspace_id: Number(workspaceID),
                }),
                text: "Create a Risk Methodology",
              },
              {
                to: generatePath(ROUTES.QUESTIONNAIRE_TEMPLATE_CREATION_PATH, {
                  workspace_id: Number(workspaceID),
                }),
                text: "Create a Questionnaire Template",
              },
              {
                to: generatePath(ROUTES.TEMPLATE_CREATION_PATH, {
                  workspace_id: Number(workspaceID),
                }),
                text: "Create an Assessment Template",
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

export default observer(RADashboardContent);
